import { assetTypeIconKind } from '../constants/homePageContent';
import type { MessageKey } from './messages';
import type { TranslateFn } from './operationLabels';
import type { AppLanguage } from './types';

const KIND_MESSAGE_KEY: Record<
  ReturnType<typeof assetTypeIconKind>,
  MessageKey | null
> = {
  all: 'home.allTypes',
  parking: 'assetType.parking',
  meeting: 'assetType.meeting',
  open: 'assetType.openSpace',
  office: 'assetType.office',
  generic: null,
};

/**
 * Exact API asset-type labels → Hebrew.
 * Checked before icon-kind fallbacks so e.g. "Private Office" is not collapsed to generic משרד.
 */
const HEBREW_BY_NORMALIZED_LABEL: Record<string, string> = {
  parking: 'חניה',
  'car park': 'חניה',
  'meeting room': 'חדר ישיבות',
  'meeting rooms': 'חדרי ישיבות',
  meeting: 'חדר ישיבות',
  'conference room': 'חדר ישיבות',
  'conference rooms': 'חדרי ישיבות',
  'training room': 'חדר כושר',
  'training rooms': 'חדרי כושר',
  'workshop space': 'מרחב סדנה',
  'workshop spaces': 'מרחבי סדנה',
  workshop: 'חדר סדנאות',
  classroom: 'כיתה',
  classrooms: 'כיתות',
  office: 'משרד',
  offices: 'משרדים',
  'private office': 'משרד פרטי',
  'private offices': 'משרדים פרטיים',
  'executive office': 'משרד מנהלים',
  'executive offices': 'משרדי מנהלים',
  'shared office': 'משרד משותף',
  'shared offices': 'משרדים משותפים',
  'serviced office': 'משרד מנוהל',
  'serviced offices': 'משרדים מנוהלים',
  'managed office': 'משרד מנוהל',
  'managed offices': 'משרדים מנוהלים',
  'virtual office': 'משרד וירטואלי',
  'virtual offices': 'משרדים וירטואליים',
  'office suite': 'סוויטת משרדים',
  'office suites': 'סוויטות משרדים',
  'team office': 'משרד צוותי',
  'team offices': 'משרדים צוותיים',
  'dedicated office': 'משרד ייעודי',
  'dedicated offices': 'משרדים ייעודיים',
  'flex office': 'משרד גמיש',
  'flex offices': 'משרדים גמישים',
  'office space': 'מרחב משרדי',
  'office spaces': 'מרחבים משרדיים',
  'small office': 'משרד קטן',
  'large office': 'משרד גדול',
  'corner office': 'משרד פינתי',
  'hot desk': 'עמדת עבודה גמישה',
  'hot desks': 'עמדות עבודה גמישות',
  'dedicated desk': 'עמדת עבודה קבועה',
  'dedicated desks': 'עמדות עבודה קבועות',
  'open space': 'אופן ספייס',
  'open workspace': 'אופן ספייס',
  gym: 'חדר כושר',
  'parking garage': 'חניון',
  'podcast studio': 'חדר פודקסטים',
  'lecture room': 'אולם הרצאות',
  clinic: 'חדר טיפול',
  'business lounge': 'טרקלין עסקים',
  'co-working': 'עבודה משותפת',
  coworking: 'עבודה משותפת',
  storage: 'אחסון',
  warehouse: 'מחסן',
  studio: 'סטודיו',
  retail: 'מסחר',
  shop: 'חנות',
  event: 'אירוע',
  'event space': 'חלל לאירועים',
  'event spaces': 'חללי אירועים',
};

function normalizeAssetTypeLabelKey(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, ' ');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Raw catalog type name on a list/search/detail record, if present. */
export function assetRecordTypeNameForList(data: Record<string, unknown>): string | null {
  const direct = data.asset_type_name ?? data.type_name ?? data.assetTypeName;
  if (typeof direct === 'string' && direct.trim()) {
    return direct.trim();
  }
  if (typeof data.asset_type === 'string' && data.asset_type.trim()) {
    return data.asset_type.trim();
  }
  if (isPlainObject(data.asset_type)) {
    for (const key of ['name', 'label', 'type_name', 'title'] as const) {
      const nested = data.asset_type[key];
      if (typeof nested === 'string' && nested.trim()) {
        return nested.trim();
      }
    }
  }
  return null;
}

/** Localized type label for list cards; null when the record has no type name. */
export function assetRecordLocalizedTypeLabel(
  data: Record<string, unknown>,
  language: AppLanguage,
  t: TranslateFn,
): string | null {
  const name = assetRecordTypeNameForList(data);
  if (!name) {
    return null;
  }
  return localizeAssetTypeLabel(name, language, t);
}

/** Display label for asset type chips and dropdowns. */
export function localizeAssetTypeLabel(
  label: string,
  language: AppLanguage,
  t: TranslateFn,
): string {
  if (language !== 'he') {
    return label;
  }
  const trimmed = label.trim();
  if (!trimmed) {
    return label;
  }
  if (/[\u0590-\u05FF]/.test(trimmed)) {
    return trimmed;
  }
  const normalized = normalizeAssetTypeLabelKey(trimmed);
  const exact = HEBREW_BY_NORMALIZED_LABEL[normalized];
  if (exact) {
    return exact;
  }
  const kindKey = KIND_MESSAGE_KEY[assetTypeIconKind(trimmed)];
  if (kindKey) {
    return t(kindKey);
  }
  return label;
}
